// ============================================================
// Viashift — Inventory page UI store (Phase 2, Epic 2A.2)
// ============================================================
// Persistent UI preferences for the Inventory page. Today this
// is just the category accordion expand/collapse state; future
// inventory-page UI prefs (sort order, filter, view density)
// land here too rather than spawning per-feature stores.
//
// Persistence: localStorage via Zustand's `persist` middleware.
// Hydration is synchronous — the store has its persisted state
// by the time React mounts, so the page never flashes "all
// expanded" before snapping to the user's last layout.
//
// Default for never-toggled categories is "expanded": that
// fallback lives at the read site (`expanded[id] ?? true`),
// not here, so the store stays a pure record of explicit user
// actions. New categories from onboarding don't require a
// store update.
//
// Scope: not cleared on logout. Categories are clinic-scoped
// UUIDs, so different clinics can't collide; users sharing a
// clinic seeing each other's accordion state is fine — it's a
// non-sensitive UI preference. See the 2A.2 entry in
// docs/DECISIONS.md for the full rationale.
//
// Versioning: storage carries a `version` for future shape
// migrations via `migrate`. Bump version + supply migrate when
// the persisted shape changes.
// ============================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface InventoryUiState {
  expanded: Record<string, boolean>;
  toggleCategory: (categoryId: string) => void;
  setCategoryExpanded: (categoryId: string, value: boolean) => void;
}

export const useInventoryUiStore = create<InventoryUiState>()(
  persist(
    (set) => ({
      expanded: {},
      toggleCategory: (categoryId) =>
        set((state) => ({
          expanded: {
            ...state.expanded,
            [categoryId]: !(state.expanded[categoryId] ?? true),
          },
        })),
      setCategoryExpanded: (categoryId, value) =>
        set((state) => ({
          expanded: { ...state.expanded, [categoryId]: value },
        })),
    }),
    {
      name: 'viashift-inventory-ui',
      version: 1,
      // Only persist data, not action functions. The factory
      // re-supplies actions on every page load.
      partialize: (state) => ({ expanded: state.expanded }),
    },
  ),
);
